import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import React from 'react';
import type { AssetWithDevices, MinimalAsset } from '../AssetsHelpGang';
import type { ColumnName } from './AssetsTable';
import { ValueWithCopyButton } from '../../components/ValueWithCopyButton';
import { AssetType } from './AssetType';
import { AccountIdWithName } from './components/AccountIdWithName';
import Tag from '@rio-cloud/rio-uikit/components/tag/Tag';
import { AssetStatus } from '../../api/assetAdministrationBackendCodec.ts';
import { CopyButton } from '../../components/CopyButton.tsx';

interface TableRowProps {
    row: AssetWithDevices;
    columns: readonly string[]; // { name: string; withCopy?: boolean }[];
    colLabels: Record<ColumnName, React.ReactNode>;
    onClick: (event: React.MouseEvent<HTMLTableRowElement>) => void;
    isActive: boolean;
    isChecked: boolean;
    isIdentificationMultipleTimesInResult: boolean;
    toggleIsChecked: () => void;
    initiateArchiveAssets: (asset: MinimalAsset[]) => void;
}

export const TableRow = React.memo(
    function TableRow(props: TableRowProps) {
        const {
            row,
            colLabels,
            onClick,
            isActive,
            isChecked,
            isIdentificationMultipleTimesInResult,
            toggleIsChecked,
            initiateArchiveAssets,
        } = props;

        return (
            <tr
                key={row.asset.id}
                data-key={row.asset.id}
                onClick={onClick}
                className={isActive ? 'active' : ''}
                data-cy='assets-table-row'
            >
                <td className='table-checkbox'>
                    <Checkbox checked={isChecked} onClick={toggleIsChecked} />
                </td>
                <td data-field={colLabels.type}>
                    <AssetType
                        assetType={row.asset.type}
                        fuelType={row.asset._embedded?.master_data?.fuel_type}
                        status={row.asset.status}
                    />
                </td>
                <td data-field={colLabels.vin}>
                    <div className={'display-flex'}>
                        {isIdentificationMultipleTimesInResult && row.asset.identification && (
                            <span
                                className='rioglyph rioglyph-duplicate rioglyph-filled text-color-warning margin-right-10'
                                title={`${row.asset.identification_type} appears multiple times in search result`}
                            />
                        )}
                        <ValueWithCopyButton value={row.asset.identification} />
                    </div>
                </td>
                <td data-field={colLabels.assetId}>
                    <ValueWithCopyButton value={row.asset.id} />
                </td>
                <td data-field={colLabels.accountId}>
                    <ValueWithCopyButton value={row.asset.account_id} tooltipText={'Copy Id'}>
                        <AccountIdWithName accountId={row.asset.account_id} />
                    </ValueWithCopyButton>
                </td>
                <td data-field={colLabels.name}>{row.asset.name}</td>
                <td data-field={colLabels.brand}>{row.asset.brand}</td>
                <td data-field={colLabels.licensePlate}>{row.asset.license_plate}</td>
                <td data-field={colLabels.dataSources}>
                    {row.devices.map(device => (
                        <div key={device.id}>
                            <Tag
                                className={'active'}
                                size={'small'}
                                style={{ userSelect: 'none', margin: '2px', padding: '2px', paddingLeft: '8px' }}
                            >
                                {device.type}: {device.identification}{' '}
                                <CopyButton value={device.identification} tooltipText={'Copy Device Identification'} />
                            </Tag>
                        </div>
                    ))}
                </td>
                <td className='table-action'>
                    {row.asset.status === AssetStatus.ACTIVE && (
                        <button
                            type={'button'}
                            className={'btn btn-primary x-asset-action-btn btn-icon-only'}
                            onClick={e => {
                                e.stopPropagation();
                                initiateArchiveAssets([
                                    {
                                        id: row.asset.id,
                                        vin: row.asset.identification,
                                        accountId: row.asset.account_id,
                                        status: row.asset.status,
                                    },
                                ]);
                            }}
                        >
                            <span className={'rioglyph rioglyph rioglyph-inbox-in'} />
                        </button>
                    )}
                </td>
            </tr>
        );
    },
    (prevProps, nextProps) => {
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);
